/*
 * HorscopeTalk live planetary transit wheel.
 * Renders an SVG zodiac wheel + a live-updating list of planet positions,
 * computed entirely client-side via horscopetalk-ephemeris.js (no network calls).
 * Supports Sidereal (Lahiri / Vedic) and Tropical (Western) display modes.
 */
(function () {
  // Plain text abbreviations are used instead of Unicode astrological glyphs (signs/planets)
  // because many browsers/OSes render those code points as full-color emoji, which breaks
  // the site's gold/navy palette unpredictably across devices.
  var SIGN_GLYPHS = ['ARI', 'TAU', 'GEM', 'CAN', 'LEO', 'VIR', 'LIB', 'SCO', 'SAG', 'CAP', 'AQU', 'PSC'];
  // The Navagraha (nine seizers of Vedic astrology): the five visible planets plus Sun, Moon,
  // and the two lunar nodes Rahu/Ketu. Uranus/Neptune/Pluto are Western-astrology-only bodies
  // and are intentionally left out of this list.
  var PLANET_ORDER = ['jupiter', 'moon', 'sun', 'mercury', 'mars', 'venus', 'rahu', 'ketu', 'saturn'];
  var PLANET_GLYPHS = { jupiter: 'Ju', moon: 'Mo', sun: 'Su', mercury: 'Me', mars: 'Ma', venus: 'Ve', rahu: 'Ra', ketu: 'Ke', saturn: 'Sa' };
  var PLANET_LABELS = {
    jupiter: 'Jupiter (Guru)', moon: 'Moon (Chandra)', sun: 'Sun (Surya)', mercury: 'Mercury (Budha)',
    mars: 'Mars (Mangala)', venus: 'Venus (Shukra)', rahu: 'Rahu', ketu: 'Ketu', saturn: 'Saturn (Shani)'
  };

  // Approximate true-to-life coloring for each body (Sun's glow, Moon's grey, Mars's rust,
  // Jupiter's tan bands, Saturn's pale gold, etc). Rahu and Ketu have no physical body -- in
  // Vedic tradition they're "shadow" grahas -- so they're rendered as smoky/eclipse-toned
  // spheres (violet-grey and dark maroon) rather than invented as a real planet color.
  // Each entry is [highlight, midtone, shadow] for a radial gradient, plus a legible label color.
  var PLANET_COLORS = {
    sun: { stops: ['#fff9e0', '#ffd24d', '#ff8a1e'], text: '#3a1d00', glow: '#ffb347' },
    moon: { stops: ['#f8f8f5', '#d2d2ca', '#97978d'], text: '#2a2a26' },
    mercury: { stops: ['#d8cab6', '#9c8b74', '#5c4c3b'], text: '#faf2e4' },
    venus: { stops: ['#fff2cc', '#e9c583', '#b6863f'], text: '#3d2800' },
    mars: { stops: ['#ffab7a', '#c1440e', '#7a2607'], text: '#fff1e8' },
    jupiter: { stops: ['#f2e0b8', '#d1a273', '#8a5a30'], text: '#2c1a05' },
    saturn: { stops: ['#f6e7bd', '#dabf87', '#a6853f'], text: '#2c1a05', ring: '#e9dba9' },
    rahu: { stops: ['#8a76bd', '#3b2f58', '#120d1e'], text: '#ece4ff' },
    ketu: { stops: ['#a3605f', '#5a2626', '#1c0a0a'], text: '#ffe6e2' }
  };

  var svgNS = 'http://www.w3.org/2000/svg';
  var cx = 230, cy = 230, outerR = 210, bandInnerR = 178, planetR = 150;
  var instanceCounter = 0;

  function angleForLon(lon) {
    // 0 deg Aries at top (12 o'clock), increasing clockwise
    return (lon - 90) * Math.PI / 180;
  }
  function polar(r, lon) {
    var a = angleForLon(lon);
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  }

  function gradId(instanceId, name) { return 'hst-grad-' + instanceId + '-' + name; }
  function glowId(instanceId) { return 'hst-glow-' + instanceId; }

  function buildDefs(svg, instanceId) {
    var defs = document.createElementNS(svgNS, 'defs');

    PLANET_ORDER.forEach(function (name) {
      var c = PLANET_COLORS[name];
      var grad = document.createElementNS(svgNS, 'radialGradient');
      grad.setAttribute('id', gradId(instanceId, name));
      grad.setAttribute('cx', '35%');
      grad.setAttribute('cy', '30%');
      grad.setAttribute('r', '70%');
      [ [0, c.stops[0]], [55, c.stops[1]], [100, c.stops[2]] ].forEach(function (pair) {
        var stop = document.createElementNS(svgNS, 'stop');
        stop.setAttribute('offset', pair[0] + '%');
        stop.setAttribute('stop-color', pair[1]);
        grad.appendChild(stop);
      });
      defs.appendChild(grad);
    });

    var blur = document.createElementNS(svgNS, 'filter');
    blur.setAttribute('id', glowId(instanceId));
    blur.setAttribute('x', '-100%'); blur.setAttribute('y', '-100%');
    blur.setAttribute('width', '300%'); blur.setAttribute('height', '300%');
    var feGaussian = document.createElementNS(svgNS, 'feGaussianBlur');
    feGaussian.setAttribute('stdDeviation', '3.2');
    blur.appendChild(feGaussian);
    defs.appendChild(blur);

    svg.appendChild(defs);
  }

  function buildWheel(svg, instanceId) {
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    buildDefs(svg, instanceId);

    for (var s = 0; s < 12; s++) {
      var startLon = s * 30, endLon = (s + 1) * 30;
      var p0 = polar(outerR, startLon), p1 = polar(outerR, endLon);
      var q0 = polar(bandInnerR, startLon), q1 = polar(bandInnerR, endLon);
      var path = document.createElementNS(svgNS, 'path');
      var d = 'M ' + q0.x + ' ' + q0.y +
              ' L ' + p0.x + ' ' + p0.y +
              ' A ' + outerR + ' ' + outerR + ' 0 0 1 ' + p1.x + ' ' + p1.y +
              ' L ' + q1.x + ' ' + q1.y +
              ' A ' + bandInnerR + ' ' + bandInnerR + ' 0 0 0 ' + q0.x + ' ' + q0.y + ' Z';
      path.setAttribute('d', d);
      path.setAttribute('class', s % 2 === 0 ? 'hst-wheel-band' : 'hst-wheel-band-alt');
      svg.appendChild(path);

      for (var t = 0; t <= 30; t += 10) {
        var lon = startLon + t;
        var isMajor = t === 0;
        var tOuter = polar(outerR, lon);
        var tInner = polar(outerR - (isMajor ? 14 : 8), lon);
        var line = document.createElementNS(svgNS, 'line');
        line.setAttribute('x1', tOuter.x); line.setAttribute('y1', tOuter.y);
        line.setAttribute('x2', tInner.x); line.setAttribute('y2', tInner.y);
        line.setAttribute('class', isMajor ? 'hst-tick-major' : 'hst-tick');
        svg.appendChild(line);
      }

      var mid = polar((outerR + bandInnerR) / 2, startLon + 15);
      var glyphText = document.createElementNS(svgNS, 'text');
      glyphText.setAttribute('x', mid.x);
      glyphText.setAttribute('y', mid.y);
      glyphText.setAttribute('class', 'hst-sign-label');
      glyphText.setAttribute('text-anchor', 'middle');
      glyphText.setAttribute('dominant-baseline', 'central');
      glyphText.textContent = SIGN_GLYPHS[s];
      svg.appendChild(glyphText);
    }

    [outerR, bandInnerR, planetR].forEach(function (r) {
      var ring = document.createElementNS(svgNS, 'circle');
      ring.setAttribute('cx', cx); ring.setAttribute('cy', cy); ring.setAttribute('r', r);
      ring.setAttribute('class', r === outerR ? 'hst-wheel-ring-outer' : 'hst-wheel-ring-inner');
      svg.appendChild(ring);
    });
  }

  function lonToText(lon) {
    // computePositions / lonToSignDegree come from horscopetalk-ephemeris.js, loaded just
    // before this file as a plain classic <script> so both share the same global scope.
    var sd = lonToSignDegree(lon);
    return sd.sign + ' ' + sd.deg + '°' + (sd.min < 10 ? '0' : '') + sd.min + "'";
  }

  function drawPlanetMarker(svg, instanceId, name, pos) {
    var c = PLANET_COLORS[name];
    var r = 13;
    var g = document.createElementNS(svgNS, 'g');
    g.setAttribute('class', 'hst-planet-marker');

    // Sun gets a soft glow halo behind it, since it's the one body that emits rather than
    // just reflects light.
    if (name === 'sun') {
      var glow = document.createElementNS(svgNS, 'circle');
      glow.setAttribute('cx', pos.x); glow.setAttribute('cy', pos.y); glow.setAttribute('r', r + 7);
      glow.setAttribute('fill', c.glow);
      glow.setAttribute('opacity', '0.55');
      glow.setAttribute('filter', 'url(#' + glowId(instanceId) + ')');
      g.appendChild(glow);
    }

    // Saturn gets a tilted ring behind the sphere.
    if (name === 'saturn' && c.ring) {
      var ring = document.createElementNS(svgNS, 'ellipse');
      ring.setAttribute('cx', pos.x); ring.setAttribute('cy', pos.y);
      ring.setAttribute('rx', r * 1.85); ring.setAttribute('ry', r * 0.55);
      ring.setAttribute('transform', 'rotate(-18 ' + pos.x + ' ' + pos.y + ')');
      ring.setAttribute('fill', 'none');
      ring.setAttribute('stroke', c.ring);
      ring.setAttribute('stroke-width', '2');
      ring.setAttribute('opacity', '0.85');
      g.appendChild(ring);
    }

    var circle = document.createElementNS(svgNS, 'circle');
    circle.setAttribute('cx', pos.x); circle.setAttribute('cy', pos.y); circle.setAttribute('r', r);
    circle.setAttribute('fill', 'url(#' + gradId(instanceId, name) + ')');
    circle.setAttribute('stroke', c.stops[2]);
    circle.setAttribute('stroke-width', '1');
    g.appendChild(circle);

    var text = document.createElementNS(svgNS, 'text');
    text.setAttribute('x', pos.x); text.setAttribute('y', pos.y);
    text.setAttribute('fill', c.text);
    text.textContent = PLANET_GLYPHS[name];
    g.appendChild(text);

    svg.appendChild(g);
  }

  function planetSwatchStyle(name) {
    var c = PLANET_COLORS[name];
    return 'background: radial-gradient(circle at 35% 30%, ' + c.stops[0] + ', ' + c.stops[1] + ' 55%, ' + c.stops[2] + ' 100%); color: ' + c.text + '; border-color: ' + c.stops[2] + ';';
  }

  function initWidget(root, index) {
    var instanceId = root.getAttribute('data-hst-instance') || String(index);
    var svg = root.querySelector('[data-hst-wheel]');
    var listEl = root.querySelector('[data-hst-list]');
    var clockEl = root.querySelector('[data-hst-clock]');
    var ayanamsaEl = root.querySelector('[data-hst-ayanamsa]');
    var btnSidereal = root.querySelector('[data-hst-mode="sidereal"]');
    var btnTropical = root.querySelector('[data-hst-mode="tropical"]');
    if (!svg || !listEl) return;

    var mode = 'sidereal';
    var timer = null;

    function render() {
      var now = new Date();
      var result = computePositions(now);
      var bodies = result.bodies;
      var ayanamsa = result.ayanamsa;

      if (ayanamsaEl) {
        ayanamsaEl.textContent = 'Lahiri ayanamsa: ' + ayanamsa.toFixed(2) + '° — positions update automatically as the planets move';
      }
      if (clockEl) {
        clockEl.textContent = now.toUTCString().replace('GMT', 'UTC');
      }

      var existingMarkers = svg.querySelectorAll('.hst-planet-marker');
      existingMarkers.forEach(function (el) { svg.removeChild(el); });

      listEl.innerHTML = '';
      var usedAngles = [];

      PLANET_ORDER.forEach(function (name) {
        var lon = mode === 'sidereal' ? bodies[name].sidereal : bodies[name].tropical;
        var nudge = 0;
        usedAngles.forEach(function (u) {
          var diff = Math.abs(((lon - u + 540) % 360) - 180);
          if (diff < 6) nudge += 16;
        });
        usedAngles.push(lon);
        var pos = polar(planetR - nudge, lon);

        drawPlanetMarker(svg, instanceId, name, pos);

        var row = document.createElement('div');
        row.className = 'hst-planet-row';
        row.innerHTML =
          '<div class="hst-planet-glyph-label">' +
            '<div class="hst-planet-glyph" style="' + planetSwatchStyle(name) + '">' + PLANET_GLYPHS[name] + '</div>' +
            '<div class="hst-planet-name">' + PLANET_LABELS[name] + '</div>' +
          '</div>' +
          '<div class="hst-planet-pos">' + lonToText(lon) + '</div>';
        listEl.appendChild(row);
      });
    }

    function setMode(newMode) {
      mode = newMode;
      if (btnSidereal) btnSidereal.classList.toggle('active', mode === 'sidereal');
      if (btnTropical) btnTropical.classList.toggle('active', mode === 'tropical');
      render();
    }

    if (btnSidereal) btnSidereal.addEventListener('click', function () { setMode('sidereal'); });
    if (btnTropical) btnTropical.addEventListener('click', function () { setMode('tropical'); });

    buildWheel(svg, instanceId);
    render();

    // Recompute periodically to reflect real-time planetary motion.
    // Positions move very slowly (fractions of a degree per hour) so a 30s cadence keeps
    // the display "live" without doing unnecessary work.
    timer = window.setInterval(render, 30000);

    // Pause the timer when the tab isn't visible, resume (with an immediate refresh) when it is.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (timer) { window.clearInterval(timer); timer = null; }
      } else if (!timer) {
        render();
        timer = window.setInterval(render, 30000);
      }
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-hst-transits-root]');
    roots.forEach(function (root) {
      instanceCounter += 1;
      initWidget(root, instanceCounter);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
